// The summary printed at the end of the weekly billing sheet.
//
// Claimed appearances are grouped by appearance type with their count and dollar value;
// everything that is not a money claim is counted by its action. "Claimed" follows
// isClaimedAction: an appearance type, Claimed, or Billed and closed.

import { billingAppearanceType, billingFeeFor, isClaimedAction } from "./billingConstants.js";
import { prepClaimsTotal } from "./prepClaims.js";

const NO_ACTION = "No action set";

// prepLines are the week's preparation claims (src/lib/prepClaims.js): counted and totalled
// separately, then added to the appearances for the week's total.
export function buildWeekSummary(entries = [], actions = {}, fees = {}, prepLines = []) {
  const claimedByType = new Map();
  const notClaimedByAction = new Map();
  let claimedCount = 0;
  let claimedTotal = 0;
  let claimedWithoutFee = 0;

  for (const entry of entries) {
    const action = actions[entry.id];
    if (isClaimedAction(action)) {
      claimedCount += 1;
      const type = billingAppearanceType(entry, action) || "Type not recorded";
      const fee = billingFeeFor(entry, action, fees[entry.id]);
      const row = claimedByType.get(type) || { type, count: 0, total: 0, withoutFee: 0 };
      row.count += 1;
      if (fee == null || Number.isNaN(fee)) {
        row.withoutFee += 1;
        claimedWithoutFee += 1;
      } else {
        row.total += fee;
        claimedTotal += fee;
      }
      claimedByType.set(type, row);
    } else {
      const label = action || NO_ACTION;
      notClaimedByAction.set(label, (notClaimedByAction.get(label) || 0) + 1);
    }
  }

  const round = (value) => Math.round(value * 100) / 100;
  const claimed = [...claimedByType.values()]
    .map(row => ({ ...row, total: round(row.total) }))
    .sort((a, b) => b.total - a.total || b.count - a.count || a.type.localeCompare(b.type));
  // "No action set" last, so the staff choices read first.
  const notClaimed = [...notClaimedByAction.entries()]
    .map(([action, count]) => ({ action, count }))
    .sort((a, b) => (a.action === NO_ACTION) - (b.action === NO_ACTION) || b.count - a.count || a.action.localeCompare(b.action));

  const prepCount = prepLines.length;
  const prepTotal = round(prepClaimsTotal(prepLines));

  return {
    totalAppearances: entries.length,
    claimedCount,
    claimedTotal: round(claimedTotal),
    prepCount,
    prepTotal,
    weekTotal: round(claimedTotal + prepTotal),
    claimedWithoutFee,
    claimed,
    notClaimedCount: entries.length - claimedCount,
    notClaimed,
  };
}
